import React, { useState, useEffect, useRef } from 'react';
import { api } from '../api';

// PersonPicker — Modal zum Suchen/Auswählen oder Neuanlegen einer Person
// Props:
//   onSelect(person)  — aufgerufen mit der gewählten/erstellten Person
//   onClose()         — schließt das Modal
//   excludeIds        — Array von IDs die nicht angezeigt werden sollen
//   kontaktId         — wenn gesetzt: neue Person wird direkt als AP dieses Kontakts angelegt

export default function PersonPicker({ onSelect, onClose, excludeIds = [], kontaktId }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [newP, setNewP] = useState({ name: '', funktion: '', telefon: '', mobil: '', email: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const inputRef = useRef(null);

  // Fokus beim Öffnen
  useEffect(() => { inputRef.current?.focus(); }, []);

  // Suche mit Debounce
  useEffect(() => {
    setLoading(true);
    const timer = setTimeout(() => {
      api.getPersonen(q)
        .then(ps => setResults(ps.filter(p => !excludeIds.includes(p.id))))
        .catch(() => setResults([]))
        .finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(timer);
  }, [q, excludeIds]);

  const handleCreate = async () => {
    if (!newP.name.trim()) { setError('Name ist erforderlich.'); return; }
    setSaving(true); setError('');
    try {
      let person;
      if (kontaktId) {
        // Direkt als AP dieses Kontakts anlegen
        person = await api.createAnsprechpartner(kontaktId, newP);
      } else {
        // Standalone in zentraler DB anlegen
        person = await api.createPerson(newP);
      }
      onSelect(person);
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 500 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <span className="modal-title">Person auswählen</span>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">

          {/* Suche */}
          <input
            ref={inputRef}
            className="form-input"
            placeholder="Name, Telefon oder E-Mail suchen…"
            value={q}
            onChange={e => setQ(e.target.value)}
          />

          {/* Suchergebnisse */}
          {results.length > 0 && (
            <div style={{
              marginTop: 8, maxHeight: 260, overflowY: 'auto',
              border: '1px solid var(--gray-200)', borderRadius: 8,
              background: '#fff'
            }}>
              {results.map(p => (
                <div
                  key={p.id}
                  onClick={() => onSelect(p)}
                  style={{
                    padding: '10px 14px', cursor: 'pointer',
                    borderBottom: '1px solid var(--gray-100)',
                    transition: 'background .12s'
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = 'var(--gray-50)'}
                  onMouseLeave={e => e.currentTarget.style.background = '#fff'}
                >
                  <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--gray-800)' }}>{p.name}</div>
                  {(p.funktion || p.kontakt_name) && (
                    <div style={{ fontSize: 11, color: 'var(--gray-500)', marginTop: 1 }}>
                      {[p.funktion, p.kontakt_name].filter(Boolean).join(' · ')}
                    </div>
                  )}
                  {(p.telefon || p.mobil || p.email) && (
                    <div style={{ fontSize: 11, color: 'var(--gray-400)', marginTop: 2 }}>
                      {[p.telefon, p.mobil, p.email].filter(Boolean).join(' · ')}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {!loading && results.length === 0 && q.length > 0 && (
            <div style={{ marginTop: 8, fontSize: 13, color: 'var(--gray-400)', textAlign: 'center', padding: '8px 0' }}>
              Keine Person gefunden.
            </div>
          )}

          {/* Neu anlegen */}
          {!showCreate ? (
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              style={{ marginTop: 14, width: '100%' }}
              onClick={() => setShowCreate(true)}
            >
              + Neue Person anlegen
            </button>
          ) : (
            <div style={{
              marginTop: 14, padding: '14px 16px',
              border: '1px solid var(--primary)',
              borderRadius: 8, background: 'var(--primary-light)'
            }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--primary)', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 12 }}>
                Neue Person
              </div>
              {error && <div className="alert alert-error" style={{ marginBottom: 10 }}>{error}</div>}
              <div className="form-grid">
                <div className="form-group form-full">
                  <label className="form-label">Name *</label>
                  <input className="form-input" value={newP.name} onChange={e => setNewP(p => ({ ...p, name: e.target.value }))} placeholder="Vor- und Nachname" autoFocus />
                </div>
                <div className="form-group form-full">
                  <label className="form-label">Funktion / Position</label>
                  <input className="form-input" value={newP.funktion} onChange={e => setNewP(p => ({ ...p, funktion: e.target.value }))} placeholder="z.B. Fahrer, Serviceberater" />
                </div>
                <div className="form-group">
                  <label className="form-label">Telefon</label>
                  <input className="form-input" type="tel" value={newP.telefon} onChange={e => setNewP(p => ({ ...p, telefon: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label className="form-label">Mobil</label>
                  <input className="form-input" type="tel" value={newP.mobil} onChange={e => setNewP(p => ({ ...p, mobil: e.target.value }))} />
                </div>
                <div className="form-group form-full">
                  <label className="form-label">E-Mail</label>
                  <input className="form-input" type="email" value={newP.email} onChange={e => setNewP(p => ({ ...p, email: e.target.value }))} />
                </div>
              </div>
              <div className="form-actions" style={{ marginTop: 8 }}>
                <button type="button" className="btn btn-secondary" onClick={() => { setShowCreate(false); setError(''); }}>
                  Abbrechen
                </button>
                <button type="button" className="btn btn-primary" onClick={handleCreate} disabled={saving || !newP.name.trim()}>
                  {saving ? 'Anlegen…' : 'Person anlegen & auswählen'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
